import { Flex } from '@mantine/core'
import { useRouter } from 'next/router'
import React from 'react'

import { useAnalytics } from '@flecto/analytics'
import { useTranslation } from '@flecto/translations'
import { type UNSAFE_any } from '@flecto/types'
import { FileUploader, Modal, MIME_TYPES } from '@flecto/ui'
import useMediaQuery from '@flecto/ui/lib/utils/useMediaQuery'

import {
  type UploadDocumentStatus,
  useBookingDocumentActions,
} from '../../hooks/useBookingDocumentActions'
import { UploadDocumentCard } from '../UploadDocumentCard/UploadDocumentCard'

export function ModalAddDocument() {
  const { t: tBooking } = useTranslation('saas', { keyPrefix: 'booking' })
  const track = useAnalytics('segment')
  const { isMobile } = useMediaQuery()
  const {
    handleUpload,
    view,
    uploadedFiles,
    deleteDocument,
    change,
    handleRejection,
    deleteUploaded,
    handleRetry,
    handleCancel,
  } = useBookingDocumentActions()

  React.useEffect(() => {
    track('BOOKING_DOCUMENT__NEW', {})
  }, [])

  const router = useRouter()

  const handleView = (uploadedFile: UploadDocumentStatus) => {
    view(uploadedFile.document?.url ?? '')
    track('BOOKING_DOCUMENT__VIEW', {
      booking_id: router.query.id ? Number(router.query.id) : '',
      filename: uploadedFile.document.filename_base ?? '',
      type: uploadedFile.document?.content_type ?? '',
      category: uploadedFile.document.category ?? '',
      generated: uploadedFile.document.generated ?? false,
    })
  }

  return (
    <Modal size={isMobile ? 'md' : 'xxl'} variant='default'>
      <Modal.Close />
      <Modal.Title>{tBooking('booking_documents.add_documents')}</Modal.Title>
      <Modal.Content>
        <Flex direction={'column'} gap={'md'}>
          <FileUploader
            onDrop={(files: UNSAFE_any) => handleUpload(files)}
            onReject={(files: UNSAFE_any) => handleRejection(files)}
            accept={[MIME_TYPES.jpeg, MIME_TYPES.png, MIME_TYPES.pdf]}
            multiple
          />

          {uploadedFiles?.map((uploadedFile: UploadDocumentStatus) => {
            if (uploadedFile.status === 'error') {
              return (
                <UploadDocumentCard
                  key={uploadedFile.document.id}
                  document={uploadedFile.document}
                  errorDescription={uploadedFile.error ?? ''}
                  error
                  isAdding
                  onView={() => handleView(uploadedFile)}
                  onDelete={() => deleteUploaded(uploadedFile.id)}
                  onRetry={() => handleRetry(uploadedFile.id)}
                />
              )
            } else if (uploadedFile.status === 'loading') {
              return (
                <UploadDocumentCard
                  key={uploadedFile.document.id}
                  document={uploadedFile.document}
                  onView={() => handleView(uploadedFile)}
                  loading
                  isAdding
                  onCancel={() => handleCancel(uploadedFile.id)}
                />
              )
            } else if (uploadedFile.status === 'success') {
              return (
                <UploadDocumentCard
                  key={uploadedFile.document.id}
                  document={uploadedFile.document}
                  onChange={(field, value) =>
                    change(uploadedFile.document.id as string, field, value)
                  }
                  onView={() => handleView(uploadedFile)}
                  onDelete={() =>
                    deleteDocument(uploadedFile.document.id as string)
                  }
                  isAdding
                />
              )
            } else {
              return null
            }
          })}
        </Flex>
      </Modal.Content>
    </Modal>
  )
}
